import React, { useState, useMemo, useEffect } from 'react';
import {
    Box, Typography, TextField, Button, Grid,
    CircularProgress, Alert
} from '@mui/material';
import { Search, Plus } from 'lucide-react';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { Person } from '../../types/person';
import { PersonService } from '../../services/personService';
import personFeedService from '../../services/personFeedService';
import PersonCard from './PersonCard';
import Timeline from './Timeline';
import ConfirmationDialog from '../../components/common/ConfirmationDialog'; // Importar

const PersonFeedPage: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const personService = useMemo(() => new PersonService(), []);

    // Estados
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(true); // Começa como true para a carga inicial
    const [searchError, setSearchError] = useState<string | null>(null);
    const [searchResults, setSearchResults] = useState<Person[]>([]);

    const [selectedPersonId, setSelectedPersonId] = useState<number | null>(null);
    const [isTimelineLoading, setIsTimelineLoading] = useState(false);
    const [timelineError, setTimelineError] = useState<string | null>(null);
    const [timelineEvents, setTimelineEvents] = useState<any[]>([]);

    // Estados para o diálogo de exclusão
    const [dialogOpen, setDialogOpen] = useState(false);
    const [eventToDelete, setEventToDelete] = useState<number | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const fetchPeople = async (term: string) => {
        setIsSearching(true);
        setSearchError(null);
        // Limpa a timeline ao fazer uma nova busca
        setSelectedPersonId(null);
        setTimelineEvents([]);

        try {
            const params: any = term ? { name: term, pageSize: 100 } : { pageSize: 100 };
            // Adiciona o filtro para a carga inicial
            if (!term) {
                params.hasFeedEvents = 'true';
            }
            const response = await personService.getAll(params);
            setSearchResults(response.data);
            if (response.data.length === 0) {
                setSearchError('Nenhum profissional encontrado com os critérios da busca.');
            }
        } catch (error) {
            setSearchError('Ocorreu um erro ao buscar. Tente novamente.');
        } finally {
            setIsSearching(false);
        }
    };

    // Carrega a lista inicial de pessoas
    useEffect(() => {
        fetchPeople('');
    }, [personService]);


    const handleSelectPerson = async (personId: number) => {
        if (selectedPersonId === personId) {
            setSelectedPersonId(null);
            setTimelineEvents([]);
            return;
        }

        setSelectedPersonId(personId);
        setIsTimelineLoading(true);
        setTimelineError(null);
        setTimelineEvents([]);

        try {
            const events = await personFeedService.getFeedForPerson(personId);
            setTimelineEvents(events);
        } catch (error) {
            setTimelineError('Não foi possível carregar a timeline desta pessoa.');
        } finally {
            setIsTimelineLoading(false);
        }
    };

    const handleNewEvent = () => {
        navigate('/person-feed/new');
    };

    // Funções para controlar o diálogo
    const openDeleteDialog = (eventId: number) => {
        setEventToDelete(eventId);
        setDialogOpen(true);
    };

    const closeDeleteDialog = () => {
        setEventToDelete(null);
        setDialogOpen(false);
    };

    const handleDeleteConfirm = async () => {
        if (!eventToDelete) return;
        setIsDeleting(true);
        try {
            await personFeedService.deleteEvent(eventToDelete);
            // Remove o evento da lista local para atualizar a UI instantaneamente
            setTimelineEvents(prevEvents => prevEvents.filter(event => event.id !== eventToDelete));
            closeDeleteDialog();
        } catch (error) {
            // TODO: Mostrar erro para o usuário (ex: com um snackbar)
            console.error("Erro ao deletar evento:", error);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" component="h1">
                    Feed de Pessoas
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Plus size={16} />}
                    onClick={handleNewEvent}
                    size="small"
                    sx={{ py: 0.5, px: 2, height: 36, fontWeight: 'bold', fontSize: '0.75rem', borderRadius: 1 }}
                >
                    NOVO EVENTO
                </Button>
            </Box>

            <Box sx={{ mb: 1.5, p: 1, border: `1px solid ${theme.palette.divider}`, borderRadius: theme.shape.borderRadius }}>
                <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12} sm>
                        <TextField
                            fullWidth
                            label="Buscar por Nome ou Email"
                            variant="outlined"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && fetchPeople(searchTerm)}
                            size="small"
                            margin="dense"
                            InputProps={{ style: { fontSize: '0.85rem' }, sx: { py: 0.3, height: 36 }, startAdornment: <Search size={16} style={{ marginRight: 8, color: '#666' }} />, }}
                            InputLabelProps={{ style: { fontSize: '0.85rem' } }}
                            placeholder="Digite o nome ou email do profissional..."
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        <Button
                            variant="contained"
                            onClick={() => fetchPeople(searchTerm)}
                            disabled={isSearching}
                            size="small"
                            sx={{ py: 0.5, px: 2, height: 36, fontWeight: 'bold', fontSize: '0.75rem', borderRadius: 1 }}
                            startIcon={isSearching ? <CircularProgress size={16} color="inherit" /> : <Search size={16} />}
                        >
                            {isSearching ? 'Buscando...' : 'Buscar'}
                        </Button>
                    </Grid>
                </Grid>
            </Box>

            {/* --- Seção de Resultados --- */}
            {isSearching && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {searchError && !isSearching && (
                <Alert severity="warning" sx={{ my: 2 }}>{searchError}</Alert>
            )}

            {!isSearching && searchResults.length > 0 && (
                <Box sx={{ mt: 2 }}>
                    {searchResults.map((person) => (
                        <Box key={person.id} sx={{ mb: 1 }}>
                            <PersonCard
                                person={person}
                                onSelect={() => handleSelectPerson(person.id)}
                                isSelected={selectedPersonId === person.id}
                            />
                            {selectedPersonId === person.id && (
                                <Box sx={{ 
                                    p: 2, 
                                    border: '1px solid',
                                    borderColor: 'primary.main',
                                    borderTop: 'none', 
                                    borderRadius: '0 0 8px 8px',
                                    backgroundColor: '#fafafa'
                                }}>
                                    {isTimelineLoading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}
                                    {timelineError && <Alert severity="error" sx={{ mt: 1 }}>{timelineError}</Alert>}
                                    {!isTimelineLoading && timelineEvents.length > 0 && <Timeline events={timelineEvents} onDelete={openDeleteDialog} />}
                                    {!isTimelineLoading && timelineEvents.length === 0 && !timelineError && (
                                        <Typography sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                                            Nenhum evento encontrado para esta pessoa.
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </Box>
                    ))}
                </Box>
            )}

            <ConfirmationDialog
                open={dialogOpen}
                onClose={closeDeleteDialog}
                onConfirm={handleDeleteConfirm}
                title="Confirmar Exclusão"
                content="Tem certeza que deseja excluir permanentemente este evento? Esta ação não pode ser desfeita."
                isDeleting={isDeleting}
            />
        </Box>
    );
};

export default PersonFeedPage; 